import { useState } from "react";
import { Settings, Layers, Sparkles, BarChart3, StickyNote, BookOpen, Download, Users } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import ProductionDashboard from "../components/ProductionDashboard";
import AssetManager from "../components/AssetManager";
import WritingAssistant from "./WritingAssistant";
import ProductionOverview from "./ProductionMode";
import ProductionNotepad from "../components/ProductionNotepad";
import CastRoster from "../components/CastRoster";
import StoryInput from "../components/StoryInput";
import { API } from "../lib/constants";

const TABS = [
  { id: "overview", label: "Overview", icon: Settings },
  { id: "story", label: "Story Input", icon: BookOpen },
  { id: "cast", label: "Cast Roster", icon: Users },
  { id: "assets", label: "Assets", icon: Layers },
  { id: "writer", label: "AI Writer", icon: Sparkles },
  { id: "dashboard", label: "Dashboard", icon: BarChart3 },
  { id: "notepad", label: "Notepad", icon: StickyNote },
];

export default function ProductionHub() {
  const [activeTab, setActiveTab] = useState("overview");
  const [seriesFilter, setSeriesFilter] = useState("the-red-ink-chronicles");
  const { isDark } = useTheme();

  return (
    <div className="min-h-screen pt-24 pb-20 px-6" data-testid="production-hub">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <header className="text-center mb-6">
          <p className="font-mono text-red-500 text-sm tracking-[0.3em] mb-2">COMMAND CENTER</p>
          <h1 className="font-cinzel text-4xl md:text-5xl font-bold mb-3">PRODUCTION</h1>
          <p className="text-gray-400">From script to screen — manage your entire production pipeline</p>
        </header>

        {/* Series Selector */}
        <div className="flex justify-center gap-3 mb-6" data-testid="hub-series-selector">
          {[
            { slug: "the-red-ink-chronicles", label: "The Red Ink Chronicles", badge: "PG-13" },
            { slug: "the-notarys-shadow", label: "The Notary's Shadow", badge: "17+" },
            { slug: "signed-sealed-betrayed", label: "Signed, Sealed, Betrayed", badge: "17+" }
          ].map(s => (
            <button key={s.slug} onClick={() => setSeriesFilter(s.slug)}
              className={`px-5 py-2.5 rounded-lg font-cinzel text-sm font-bold transition-all ${
                seriesFilter === s.slug
                  ? "bg-red-600 text-white shadow-lg shadow-red-600/20"
                  : "bg-white/5 text-gray-400 hover:bg-white/10"
              }`}
              data-testid={`hub-series-${s.slug}-btn`}>
              {s.label} <span className="text-[10px] ml-1 opacity-60">{s.badge}</span>
            </button>
          ))}
          <button
            onClick={async () => {
              try {
                const res = await fetch(`${API}/backup/export?series=${seriesFilter}`);
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `ris-backup-${seriesFilter || 'all'}-${new Date().toISOString().slice(0,10)}.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              } catch(e) { alert("Backup failed: " + e.message); }
            }}
            className="px-3 py-2.5 rounded-lg text-sm font-bold bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white transition-all flex items-center gap-1.5"
            data-testid="backup-btn"
            title="Download backup of all content"
          >
            <Download size={14} /> Backup
          </button>
        </div>

        {/* Tab Bar */}
        <div className="flex justify-center mb-8" data-testid="production-tabs">
          <div className="inline-flex bg-white/[0.03] border border-white/10 rounded-xl p-1.5 gap-1">
            {TABS.map(tab => {
              const isActive = activeTab === tab.id;
              return (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                    isActive
                      ? "bg-red-600 text-white shadow-lg shadow-red-600/20"
                      : "text-gray-400 hover:text-white hover:bg-white/5"
                  }`}
                  data-testid={`tab-${tab.id}`}>
                  <tab.icon size={16} />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div data-testid={`tab-content-${activeTab}`}>
          {activeTab === "overview" && <ProductionOverview embedded seriesOverride={seriesFilter} />}
          {activeTab === "story" && <StoryInput seriesOverride={seriesFilter} />}
          {activeTab === "cast" && <CastRoster seriesOverride={seriesFilter} />}
          {activeTab === "assets" && <AssetManager embedded seriesOverride={seriesFilter} />}
          {activeTab === "writer" && <WritingAssistant embedded seriesOverride={seriesFilter} />}
          {activeTab === "dashboard" && <ProductionDashboard seriesFilter={seriesFilter} />}
          {activeTab === "notepad" && <ProductionNotepad />}
        </div>
      </div>
    </div>
  );
}
